import { HttpErrorResponse } from '@angular/common/http';
import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {AssociationListResponse} from '@services/back-office/model/associationListResponse';
import {AssociationsService} from '@shared/services/associations.service';
import {SharedService} from '@shared/shared.service';
import {ToastrService} from 'ngx-toastr';
import {BehaviorSubject, catchError, EMPTY, Subject, takeUntil} from 'rxjs';
import {map} from 'rxjs/operators';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-upload-apartments-dialog',
    templateUrl: './upload-apartments-dialog.component-new.html',
    styleUrls: ['./upload-apartments-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UploadApartmentsDialogComponentNew implements OnInit, OnDestroy {
    /** Subject to destroy Subscriptions */
    private destroyed$ = new Subject<void>();
    /** Loading */
    loading$ = new BehaviorSubject(false);
    /** Form */
    uploadForm: UntypedFormGroup;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            association: AssociationListResponse,
            building?: any
        },
        public dialogRef: MatDialogRef<UploadApartmentsDialogComponentNew>,
        private associationsService: AssociationsService,
        private formBuilder: UntypedFormBuilder,
        private translateService: TranslateService,
        private toastrService: ToastrService,
    ) {
    }

    ngOnInit(): void {
        this.initForm(this.data.association.sheetId ? this.data.association.sheetId : this.data.building?.sheetId);
    }

    initForm(sheetId: string | undefined) {
        this.uploadForm = this.formBuilder.group({
            sheetId: [sheetId ? sheetId : '', [Validators.required]],
            dataType: ['APARTMENTS', [Validators.required]],
            sortingType: [false, [Validators.required]],
            resourceType: ['COLD_WATER']
        });
    }

    ngOnDestroy() {
        this.destroyed$.complete();
        this.loading$.complete();
    }

    openLink() {
        window.open('https://docs.google.com/spreadsheets/d/' + this.uploadForm.get('sheetId')?.value, '_blank');
    }

    getRequestData(type: string, upload?: boolean) {
        const formData = this.uploadForm.getRawValue();
        const query = {
            dataType: formData.dataType,
            ...formData.resourceType && {resourceType: formData.resourceType},
            sheetId: formData.sheetId,
            uploadFlag: upload,
            alternateSorting: formData.sortingType
        };

        switch (true) {
            case query.dataType === 'APARTMENTS':
                type === 'check' ? this.checkApartmentsData(query) : this.uploadApartmentsData(query);
                break;
            case query.dataType === 'USERS_APARTMENTS':
                type === 'check' ? this.fillUserApartmentData(query) : this.uploadUserApartmentData(query);
                break;
            case query.dataType === 'USERS':
                type === 'check' ? this.fillTenantsData(query) : this.uploadTenantsData(query);
                break;
            case query.dataType === 'BALANCES':
                type === 'check' ? this.fillBalancesData(query) : this.uploadBalancesData(query);
                break;
            case query.dataType === 'COUNTERS':
                type === 'check' ? this.fillCountersData(query) : this.uploadCountersData(query);
                break;
            default:
                return;
        }
    }

    checkApartmentsData(query: any) {
        this.loading$.next(true);
        this.associationsService.checkApartmentData(this.data.association.id!, query.sheetId, query.uploadFlag, query.alternateSorting, this.data?.building?.id ? this.data?.building?.id : '').pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.loading$.next(false);
                SharedService.showSuccessWithTitle(
                    this.translateService,
                    this.toastrService,
                    'modules.clients.associations.uploadApartmentsDialog.labels.success'
                );
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    uploadApartmentsData(query: any) {
        this.loading$.next(true);
        this.associationsService.uploadApartmentData(this.data.association.id!, query.sheetId, query.alternateSorting, this.data?.building?.id ? this.data?.building?.id : '').pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.loading$.next(false);
                SharedService.showSuccessWithTitle(
                    this.translateService,
                    this.toastrService,
                    'modules.clients.associations.uploadApartmentsDialog.labels.success'
                );
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    fillUserApartmentData(query: any) {
        this.loading$.next(true);
        this.associationsService.fillUserApartmentData(this.data.association.id!, query.sheetId, query.alternateSorting, this.data?.building?.id ? this.data?.building?.id : '').pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.loading$.next(false);
                SharedService.showSuccessWithTitle(
                    this.translateService,
                    this.toastrService,
                    'modules.clients.associations.uploadApartmentsDialog.labels.success'
                );
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    uploadUserApartmentData(query: any) {
        this.loading$.next(true);
        this.associationsService.uploadUserApartmentDataData(this.data.association.id!, query.sheetId, query.alternateSorting, this.data?.building?.id ? this.data?.building?.id : '').pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.loading$.next(false);
                SharedService.showSuccessWithTitle(
                    this.translateService,
                    this.toastrService,
                    'modules.clients.associations.uploadApartmentsDialog.labels.success'
                );
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    fillTenantsData(query: any) {
        this.loading$.next(true);
        this.associationsService.fillTenantsData(this.data.association.id!, query.sheetId, query.alternateSorting, this.data?.building?.id ? this.data?.building?.id : '').pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.loading$.next(false);
                SharedService.showSuccessWithTitle(
                    this.translateService,
                    this.toastrService,
                    'modules.clients.associations.uploadApartmentsDialog.labels.success'
                );
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    uploadTenantsData(query: any) {
        this.loading$.next(true);
        this.associationsService.uploadTenantsData(this.data.association.id!, query.sheetId, query.alternateSorting, this.data?.building?.id ? this.data?.building?.id : '').pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.loading$.next(false);
                SharedService.showSuccessWithTitle(
                    this.translateService,
                    this.toastrService,
                    'modules.clients.associations.uploadApartmentsDialog.labels.success'
                );
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    fillBalancesData(query: any) {
        this.loading$.next(true);
        this.associationsService.fillBalancesDataNew(this.data.association.id!, query.sheetId, query.alternateSorting, this.data?.building?.id ? this.data?.building?.id : '').pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.loading$.next(false);
                SharedService.showSuccessWithTitle(
                    this.translateService,
                    this.toastrService,
                    'modules.clients.associations.uploadApartmentsDialog.labels.success'
                );
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    uploadBalancesData(query: any) {
        this.loading$.next(true);
        this.associationsService.uploadBalancesDataNew(this.data.association.id!, query.sheetId, query.alternateSorting, this.data?.building?.id ? this.data?.building?.id : '').pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.loading$.next(false);
                SharedService.showSuccessWithTitle(
                    this.translateService,
                    this.toastrService,
                    'modules.clients.associations.uploadApartmentsDialog.labels.success'
                );
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    fillCountersData(query: any) {
        this.loading$.next(true);
        this.associationsService.fillCountersDataNew(this.data.association.id!, query.sheetId, query.alternateSorting, query.resourceType, this.data?.building?.id ? this.data?.building?.id : '').pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.loading$.next(false);
                SharedService.showSuccessWithTitle(
                    this.translateService,
                    this.toastrService,
                    'modules.clients.associations.uploadApartmentsDialog.labels.success'
                );
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    uploadCountersData(query: any) {
        this.loading$.next(true);
        this.associationsService.uploadCountersDataNew(this.data.association.id!, query.sheetId, query.alternateSorting, query.resourceType, this.data?.building?.id ? this.data?.building?.id : '').pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.loading$.next(false);
                SharedService.showSuccessWithTitle(
                    this.translateService,
                    this.toastrService,
                    'modules.clients.associations.uploadApartmentsDialog.labels.success'
                );
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    /** Close dialog */
    closeDialog(result: boolean) {
        this.dialogRef.close(result ? 'Confirm' : 'Cancel');
    }

}
